import React, { useState, useEffect } from "react";
import {
	OperationalConfigurationOverride,
	OperationalConfigurationOverrideEditRequest,
	OperationalConfigurationOverrideEditRequestInitialValue,
	OperationalConfigurationOverrideEditRequestValidationSchema,
	Environment,
	ProductInstance,
	Component,
	ComponentInstance,
} from "../../models/OperationalConfiguration";
import { Formik } from "formik";
import { FormikTextInputField } from "../Form/FormikTextInput";
import { useSnackbar } from "notistack";
import { showSnack } from "../../utils/component";
import {
	addOperationalConfigurationOverride,
	editOperationalConfigurationOverride,
	getEnvironments,
	getProductInstances,
	getComponents,
	getComponentInstances,
} from "../../services/api/operationalConfiguration.api";
import { Button } from "@material-ui/core";
import { tryGetProp } from "../../utils";

interface OperationalConfigurationOverrideEditProps {
	// optional operational configuration override object
	// if not provided, form will be displayed in add mode
	operationalConfigurationOverride?: OperationalConfigurationOverride | null;
	// product id
	productId: string;
	// id of parent operational config
	operationalConfigurationId: string;
	// function to call on successful save
	onSuccess?: () => void;
	// function to call on cancel
	onCancel?: () => void;
}

/**
 * Display an operational configuration override
 * @param props
 */
export const OperationalConfigurationOverrideEdit = (props: OperationalConfigurationOverrideEditProps) => {
	const { enqueueSnackbar } = useSnackbar();
	const [isFormSubmitting, setIsFormSubmitting] = useState(false);

	const [environments, setEnvironments] = useState<Environment[]>([]);
	const [productInstances, setProductInstances] = useState<ProductInstance[]>([]);
	const [components, setComponents] = useState<Component[]>([]);
	const [componentInstances, setComponentInstances] = useState<ComponentInstance[]>([]);
	const [loadedLookupData, setLoadedLookupData] = useState(false);

	useEffect(() => {
		const getData = async () => {
			const environmentPromise = getEnvironments(props.productId)();
			const productInstancePromise = getProductInstances(props.productId)();
			const componentPromise = getComponents(props.productId)();
			const componentInstancePromise = getComponentInstances(props.productId)();

			const environmentResponse = await environmentPromise;
			const productInstanceResponse = await productInstancePromise;
			const componentResponse = await componentPromise;
			const componentInstanceResponse = await componentInstancePromise;

			setEnvironments(tryGetProp([], () => environmentResponse.data));
			setProductInstances(tryGetProp([], () => productInstanceResponse.data));
			setComponents(tryGetProp([], () => componentResponse.data));
			setComponentInstances(tryGetProp([], () => componentInstanceResponse.data));

			setLoadedLookupData(true);
		};

		getData();
	}, [props.productId]);

	const isEditMode = props.operationalConfigurationOverride != null;

	const initialValues: OperationalConfigurationOverrideEditRequest = {
		operationalConfigurationOverrideId: tryGetProp(
			OperationalConfigurationOverrideEditRequestInitialValue.operationalConfigurationOverrideId!,
			() => props.operationalConfigurationOverride!.operationalConfigurationOverrideId,
		),
		operationalConfigurationId: props.operationalConfigurationId,
		environmentId: tryGetProp(
			OperationalConfigurationOverrideEditRequestInitialValue.environmentId!,
			() => props.operationalConfigurationOverride!.environmentId,
		),
		productInstanceId: tryGetProp(
			OperationalConfigurationOverrideEditRequestInitialValue.productInstanceId!,
			() => props.operationalConfigurationOverride!.productInstanceId,
		),
		componentId: tryGetProp(
			OperationalConfigurationOverrideEditRequestInitialValue.componentId!,
			() => props.operationalConfigurationOverride!.componentId,
		),
		componentInstanceId: tryGetProp(
			OperationalConfigurationOverrideEditRequestInitialValue.componentInstanceId!,
			() => props.operationalConfigurationOverride!.componentInstanceId,
		),
		value: tryGetProp(
			OperationalConfigurationOverrideEditRequestInitialValue.value,
			() => props.operationalConfigurationOverride!.value,
		),
	};

	const submitForm = async (values, { resetForm }) => {
		try {
			setIsFormSubmitting(true);

			if (isEditMode) {
				await editOperationalConfigurationOverride(
					props.productId,
					props.operationalConfigurationOverride!.operationalConfigurationOverrideId,
					values,
				)();
			} else {
				await addOperationalConfigurationOverride(props.productId, values)();
				resetForm();
			}

			showSnack(enqueueSnackbar, "Changes saved successfully");

			if (props.onSuccess) {
				props.onSuccess();
			}
		} catch (error: any) {
			console.log({ error });

			if (tryGetProp(0, () => error.response.status) === 409) {
				showSnack(
					enqueueSnackbar,
					"Validation error, please make sure an override with the same values doesn't already exist",
					"error",
				);
			} else {
				showSnack(enqueueSnackbar, "There was an error while saving changes", "error");
			}
		}

		setIsFormSubmitting(false);
	};

	return (
		<div>
			{loadedLookupData && (
				<Formik
					initialValues={initialValues}
					validationSchema={OperationalConfigurationOverrideEditRequestValidationSchema}
					onSubmit={submitForm}
				>
					{({ handleSubmit }) => {
						return (
							<div style={{ marginTop: 30 }}>
								<FormikTextInputField
									name="environmentId"
									label="Environment"
									optimized={false}
									isSelect={true}
									selectOptions={environments.map((x) => ({
										val: x.id,
										text: x.name,
									}))}
									appendEmptySelectOption={true}
								/>

								<FormikTextInputField
									name="productInstanceId"
									label="Product instance"
									optimized={false}
									isSelect={true}
									selectOptions={productInstances.map((x) => ({
										val: x.id,
										text: x.name,
									}))}
									appendEmptySelectOption={true}
								/>

								<FormikTextInputField
									name="componentId"
									label="Component"
									optimized={false}
									isSelect={true}
									selectOptions={components.map((x) => ({
										val: x.id,
										text: x.name,
									}))}
									appendEmptySelectOption={true}
								/>

								<FormikTextInputField
									name="componentInstanceId"
									label="Component instance"
									optimized={false}
									isSelect={true}
									selectOptions={componentInstances.map((x) => ({
										val: x.id,
										text: x.name,
									}))}
									appendEmptySelectOption={true}
								/>

								<FormikTextInputField name="value" label="Value" optimized={true} />

								<div style={{ marginTop: 20 }}>
									<Button
										data-testid="edit-operational-configuration-override-save-btn"
										variant="contained"
										disabled={isFormSubmitting}
										color="primary"
										style={{ marginRight: 12, marginTop: 10 }}
										onClick={() => handleSubmit()}
									>
										{isEditMode ? "Save override" : "Add override"}
									</Button>
									<Button
										data-testid="edit-operational-configuration-override-cancel-btn"
										disabled={isFormSubmitting}
										style={{ marginRight: 12, marginTop: 10 }}
										onClick={() => props.onCancel && props.onCancel()}
									>
										Cancel
									</Button>
								</div>
							</div>
						);
					}}
				</Formik>
			)}
		</div>
	);
};
